/**
 * Colour arithmetic, for the two questions the accounts screen has to answer.
 *
 * Both of them arrived with the custom colour picker, and neither can be
 * answered by a stylesheet. The first is *what does this colour look like in
 * the other theme* — the eight built-in colours have a hand-tuned pair each,
 * and a colour somebody just chose has one value and needs a second. The second
 * is *can these two accounts be told apart* — which is the property the eight
 * were selected for and the property a colour wheel cannot promise.
 *
 * Kept apart from `palette.ts` on purpose. This file is arithmetic with no
 * opinions: sRGB in, sRGB out, and every function here would give the same
 * answer in any application. The opinions — which lightness a colour is pinned
 * to, how far apart two of them have to be before anyone is warned — are policy
 * and live next to the palette they are policy about.
 *
 * # Which colour spaces, and why more than one
 *
 * Two, because they are good at different things and neither is good at both.
 *
 * OKLCH does the lightness work. Its L is perceptual and, unlike CIELAB's, it
 * is close to uniform across hues: pinning a yellow and a blue to the same L in
 * OKLab produces two colours that genuinely look equally light, where doing it
 * in CIELAB or in HSL does not. That is exactly the operation the theming needs,
 * because "make this readable on a near-black background" is a statement about
 * lightness and must not become a statement about hue.
 *
 * CIELAB with a plain ΔE does the distance work, for a duller reason: it is
 * what the palette was derived in. `hm_store::colour` quotes ΔE 26 and ΔE 23 as
 * the closest pairs among the eight, and a warning threshold is only meaningful
 * next to those numbers. Measuring in a different space would produce a number
 * nobody could compare to the ones already written down.
 *
 * # Dichromacy
 *
 * The Viénot, Brettel and Mollon 1999 model, the same one the palette was
 * derived under: a 3x3 matrix applied to *linear* RGB, one for protanopia and
 * one for deuteranopia. It is an approximation and a well-understood one — it
 * collapses the red-green axis onto the surviving confusion line — and it is
 * used here for the only thing an approximation is safe for, which is telling
 * somebody that two colours may be hard to distinguish. Nothing refuses on it.
 */

/** Linear-light sRGB, each channel 0..1. The space the matrices work in. */
export type Linear = readonly [number, number, number];

/** A colour in cylindrical OKLab: lightness 0..1, chroma, hue in degrees. */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/**
 * Parse `#rrggbb` into linear-light sRGB, or null.
 *
 * Six digits only, and no attempt to understand `rgb()` or a colour name. The
 * one caller that has to be forgiving — a text box a person types into — does
 * its forgiving before it gets here, so that everything downstream of the parse
 * is dealing with one syntax.
 */
export function parseHex(hex: string): Linear | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const n = Number.parseInt(hex.slice(1), 16);
  return [
    toLinear(((n >> 16) & 0xff) / 255),
    toLinear(((n >> 8) & 0xff) / 255),
    toLinear((n & 0xff) / 255),
  ];
}

/** Back to `#rrggbb`, lowercase, clamping anything outside the gamut. */
export function toHex(rgb: Linear): string {
  const hex = rgb
    .map((c) => {
      const v = Math.round(Math.min(1, Math.max(0, fromLinear(c))) * 255);
      return v.toString(16).padStart(2, "0");
    })
    .join("");
  return `#${hex}`;
}

export function oklchOf(rgb: Linear): Oklch {
  const [l, a, b] = oklab(rgb);
  const c = Math.hypot(a, b);
  // Hue is meaningless at zero chroma and comes back as whatever the arctangent
  // of two zeros happens to be. Reported as 0 so that a grey stays a grey
  // through a round trip instead of acquiring a hue on the way out.
  const h = c < 1e-6 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

/**
 * An OKLCH colour back in sRGB, brought into gamut by giving up chroma.
 *
 * Lightness and hue are held and chroma is bisected down until the result fits,
 * which is the standard gamut-mapping compromise and the right one here: the
 * lightness is the whole point — it is what makes the colour readable on the
 * background it is going to be drawn on — and the hue is what the person
 * choosing the colour actually chose. Chroma is the one of the three nobody
 * asked for by name.
 *
 * Sixteen halvings, which is far more precision than eight bits per channel can
 * show and cheap enough not to think about.
 */
export function fromOklch({ l, c, h }: Oklch): Linear {
  if (inGamut(oklchToLinear(l, c, h))) return oklchToLinear(l, c, h);
  let low = 0;
  let high = c;
  for (let i = 0; i < 16; i += 1) {
    const mid = (low + high) / 2;
    if (inGamut(oklchToLinear(l, mid, h))) low = mid;
    else high = mid;
  }
  return oklchToLinear(l, low, h);
}

/**
 * How far apart two colours are, to the eye least able to tell them apart.
 *
 * The minimum over normal, protanope and deuteranope vision — the same "worst
 * of the three" the palette was measured with, and the only sensible direction
 * to be wrong in: a pair that is obvious to most people and identical to one in
 * twelve is a pair this should report as close.
 */
export function separation(a: Linear, b: Linear): number {
  return Math.min(
    deltaE(a, b),
    deltaE(simulate(a, PROTAN), simulate(b, PROTAN)),
    deltaE(simulate(a, DEUTAN), simulate(b, DEUTAN)),
  );
}

/**
 * The WCAG contrast ratio between two colours, 1 to 21.
 *
 * A third measure of lightness in a file that already has two, and it is here
 * because it is the one the *requirement* is written in: the stylesheet holds
 * the account colours to 3:1 against the surfaces they are drawn on, and that
 * number means this formula and no other. Perceptual lightness is not a
 * substitute -- a saturated yellow and a saturated blue at the same OKLab L
 * differ by a factor of three in relative luminance, which is exactly the case
 * that made the difference matter here.
 */
export function contrast(a: Linear, b: Linear): number {
  const luminance = ([r, g, b2]: Linear): number => 0.2126 * r + 0.7152 * g + 0.0722 * b2;
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Plain CIE76 ΔE, in the space the palette's own numbers are quoted in. */
function deltaE(a: Linear, b: Linear): number {
  const [l1, a1, b1] = lab(a);
  const [l2, a2, b2] = lab(b);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

// ---- the conversions ---------------------------------------------------------

const toLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

const fromLinear = (c: number): number =>
  c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;

/** A hair of slack, so a colour that is in gamut to eight bits is not rejected
    by a rounding error in the seventeenth. */
function inGamut([r, g, b]: Linear): boolean {
  return [r, g, b].every((c) => c >= -1e-4 && c <= 1 + 1e-4);
}

function oklab([r, g, b]: Linear): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklchToLinear(lightness: number, chroma: number, hue: number): Linear {
  const rad = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(rad);
  const b = chroma * Math.sin(rad);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** CIELAB against D65, which is what sRGB is already in — so no adaptation. */
function lab([r, g, b]: Linear): [number, number, number] {
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.9504559;
  const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.0890578;
  const f = (t: number): number =>
    t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116;
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

/**
 * Viénot 1999, in linear RGB. Row-major, one matrix per kind of dichromacy.
 *
 * Written out rather than derived through LMS at runtime, because these are the
 * published constants that the palette's own ΔE figures were computed with, and
 * a rederivation that differed in the fourth decimal would silently make this
 * file's numbers incomparable with the ones in `hm_store::colour`.
 */
const PROTAN = [0.11238, 0.88762, 0, 0.11238, 0.88762, 0, 0.00401, -0.00401, 1] as const;
const DEUTAN = [0.29275, 0.70725, 0, 0.29275, 0.70725, 0, -0.02234, 0.02234, 1] as const;

function simulate([r, g, b]: Linear, m: readonly number[]): Linear {
  const at = (i: number): number => m[i] ?? 0;
  return [
    at(0) * r + at(1) * g + at(2) * b,
    at(3) * r + at(4) * g + at(5) * b,
    at(6) * r + at(7) * g + at(8) * b,
  ];
}
